import { assertUnreachable } from '@shared/helpers/function.helpers';

export type RightPanelTab = 'agent' | 'properties' | 'actions';
export type BuildRightPanelTab = RightPanelTab | 'comments';

export const panelTabs = (
  showProperties: boolean,
  proposalSelected: boolean,
): readonly RightPanelTab[] => [
  'agent',
  ...(showProperties ? (['properties'] as const) : []),
  ...(proposalSelected ? (['actions'] as const) : []),
];

export const buildPanelTabOrder = (
  showProperties: boolean,
  proposalSelected: boolean,
): readonly BuildRightPanelTab[] => [
  ...panelTabs(showProperties, proposalSelected),
  'comments',
];

/** Tab to keep when navigation would open Actions on a proposal that has none. */
const tabWhenProposalHasNoActions = (
  current: BuildRightPanelTab,
  propertiesVisible: boolean,
): BuildRightPanelTab => {
  switch (current) {
    case 'agent':
    case 'comments':
      return current;
    case 'properties':
      if (propertiesVisible) return current;
      return 'agent';
    case 'actions':
      return 'agent';
    default:
      return assertUnreachable(current);
  }
};

export type BuildPanelSelection = {
  selectedProposalId: string | undefined;
  proposalHasActions: boolean;
  elementSelected: boolean;
  /** An element was selected on the previous render and is not anymore. */
  elementCleared: boolean;
  /** An SBEF group row is the list selection. */
  groupSelected: boolean;
  /** Element or proposal selection differs from the previous render. */
  selectionChanged: boolean;
  /** A proposal became the selection on this render. */
  proposalSelected: boolean;
  /** The baseline became the selection on this render. */
  baselineChosen: boolean;
  /** The already selected proposal was clicked again. */
  actionsRequested: boolean;
};

/** Tab the build panel moves to after a selection change or proposal click. */
export const nextBuildPanelTab = (
  tab: BuildRightPanelTab,
  selection: BuildPanelSelection,
): BuildRightPanelTab => {
  const { selectedProposalId, elementSelected, groupSelected } = selection;
  const baselineSelected = selectedProposalId === undefined;
  const showProperties =
    !groupSelected && (elementSelected || baselineSelected);
  let nextTab = tab;
  let autoSelectedActions = false;
  if (!showProperties && nextTab === 'properties') {
    nextTab = 'actions';
    autoSelectedActions = true;
  }
  if (nextTab === 'actions' && baselineSelected && !groupSelected) {
    nextTab = 'properties';
  }
  if (selection.selectionChanged && elementSelected) {
    nextTab = 'properties';
  }
  const opensActions =
    (selection.proposalSelected && !elementSelected) ||
    (selection.elementCleared && !baselineSelected) ||
    (selection.actionsRequested && !baselineSelected && !elementSelected);
  if (opensActions) {
    nextTab = 'actions';
    autoSelectedActions = true;
  }
  if (selection.baselineChosen) {
    nextTab = 'properties';
  }
  if (
    autoSelectedActions &&
    nextTab === 'actions' &&
    !selection.proposalHasActions
  ) {
    nextTab = tabWhenProposalHasNoActions(tab, showProperties);
  }
  return nextTab;
};

/**
 * Shared links hide Agent and History. A group on the baseline then has no
 * tab: properties need an element, actions need a proposal.
 */
export const resolveSharedBuildPanelTab = (
  tab: BuildRightPanelTab,
  options: {
    isSharedProject: boolean;
    showProperties: boolean;
    proposalId: string | undefined;
  },
): BuildRightPanelTab | 'empty' => {
  if (!options.isSharedProject) return tab;
  if (tab !== 'agent' && tab !== 'comments') return tab;
  if (options.showProperties) return 'properties';
  if (options.proposalId) return 'actions';
  return 'empty';
};
